import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { smart_meters_base_url } from "../../../../../../utils/urls";
import { toastify } from '../../../../../../utils/toast';

const TransactionsTab = ({ meterId }) => {
    // Get meterId from URL if not provided as prop
    const getMeterId = () => {
        if (meterId) return meterId;
        
        // Try to get from URL path
        const pathParts = window.location.pathname.split('/');
        const meterIdFromUrl = pathParts[pathParts.length - 1];
        return meterIdFromUrl;
    };
    
    const actualMeterId = getMeterId();
    
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [meterDetails, setMeterDetails] = useState(null);

    // Fetch meter details to get customer ID and account number
    useEffect(() => {
        if (actualMeterId) {
            fetchMeterDetails();
        }
    }, [actualMeterId]);

    // Fetch customer transactions when meter details are available
    useEffect(() => {
        if (meterDetails?.accountNumber) {
            fetchCustomerTransactions(meterDetails.accountNumber, meterDetails.customerId);
        }
    }, [meterDetails]);

    // Add event listener for meter updates (to refresh transactions when meter is updated)
    useEffect(() => {
        const handleMeterUpdate = (event) => {
            if (event.detail.meterId === actualMeterId) {
                fetchMeterDetails();
            }
        };

        window.addEventListener('meter-updated', handleMeterUpdate);

        return () => {
            window.removeEventListener('meter-updated', handleMeterUpdate);
        };
    }, [actualMeterId]);

    // Fetch meter details to get customer ID and account number
    const fetchMeterDetails = async () => {
        try {
            const response = await makeRequest2(
                `/api/app/water-meters/details/${actualMeterId}`,
                'GET',
                null
            );

            if (response.success || response.meter) { // Handle both cases
                const meterData = response.meter || response.data?.meter;
                setMeterDetails(meterData);
            } else {
                toastify('Failed to fetch meter details', 'error');
            }
        } catch (error) {
            toastify('Error fetching meter details', 'error');
        }
    };

    // Fetch customer transactions using account number and customer ID
    const fetchCustomerTransactions = async (accountNumber, customerId) => {
        try {
            setLoading(true);
            
            // Build the URL with account number and customer ID as query parameter
            let url = `/api/app/customer-account/transactions/${accountNumber}`;
            
            // Add customerId as query parameter if it exists
            if (customerId) {
                // Handle ObjectId format
                const customerIdValue = typeof customerId === 'object' && customerId.$oid 
                    ? customerId.$oid 
                    : customerId;
                url += `?customerId=${customerIdValue}`;
            }

            const response = await makeRequest2(url, 'GET', null);

            if (response.success) {
                // Updated to match the new response structure
                const fetchedTransactions = response.data.transactions || [];
                setTransactions(fetchedTransactions);
            } else {
                toastify(response.error || 'Failed to fetch transactions', 'error');
                setTransactions([]);
            }
        } catch (error) {
            toastify('Error fetching transactions', 'error');
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    // Format amount column
    const amountBodyTemplate = (rowData) => {
        return `KES ${rowData.amount?.toFixed(2) || '0.00'}`;
    };

    // Format date column
    const dateBodyTemplate = (rowData) => {
        if (!rowData.transTime && !rowData.createdAt) return 'N/A';
        
        // Handle transTime format (20250502003018) or use createdAt
        const dateToFormat = rowData.transTime 
            ? rowData.transTime 
            : rowData.createdAt;
            
        if (rowData.transTime && rowData.transTime.length === 14) {
            // Parse transTime format: YYYYMMDDHHMMSS
            const year = dateToFormat.substring(0, 4);
            const month = dateToFormat.substring(4, 6);
            const day = dateToFormat.substring(6, 8);
            const hour = dateToFormat.substring(8, 10);
            const minute = dateToFormat.substring(10, 12);
            const second = dateToFormat.substring(12, 14);
            
            const formattedDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
            return formattedDate.toLocaleString();
        }
        
        return new Date(dateToFormat).toLocaleString();
    };

    // Format transaction ID
    const transactionIdTemplate = (rowData) => {
        return rowData.mpesaReceiptNumber || rowData._id || 'N/A';
    };

    // Format customer name
    const customerNameTemplate = (rowData) => {
        return rowData.CustomerInfo?.fullName || 'N/A';
    };

    // Format customer phone number
    const customerPhoneTemplate = (rowData) => {
        return rowData.CustomerInfo?.phoneNumber || 'N/A';
    };

    return (
        <div className="row">
            <div className="col-sm-12">
                <div className="card">
                    <div className="card-body">
                        <DataTable
                            value={transactions}
                            loading={loading}
                            paginator
                            rows={10}
                            stripedRows
                            emptyMessage={loading ? "Loading transactions..." : "No transactions found for this meter"}
                            tableStyle={{ minWidth: '50rem' }}
                            sortMode="single"
                        >
                            <Column 
                                field="mpesaReceiptNumber" 
                                header="Transaction ID" 
                                body={transactionIdTemplate}
                                sortable 
                            />
                            <Column 
                                field="accountNumber" 
                                header="Account Number" 
                                sortable 
                            />
                            <Column 
                                field="CustomerInfo.fullName" 
                                header="Customer Name" 
                                body={customerNameTemplate}
                                sortable 
                            />
                            <Column 
                                field="CustomerInfo.phoneNumber" 
                                header="Phone Number" 
                                body={customerPhoneTemplate}
                                sortable 
                            />
                            <Column 
                                field="amount" 
                                header="Amount" 
                                body={amountBodyTemplate}
                                sortable 
                            />
                            <Column 
                                field="transTime" 
                                header="Date" 
                                body={dateBodyTemplate}
                                sortable 
                            />
                        </DataTable>
                       
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionsTab;